import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getApiUrl, API_CONFIG } from "../config/api";
import ReviewCard from "../components/ReviewCard";
import { useReviewAnalysis } from "../hooks/useReviewAnalysis";
import { useReviewResponse } from "../hooks/useReviewResponse";
import {
  Review,
  OzonReviewsResponse,
  WildberriesReviewsResponse,
} from "../types";
import "../styles/newReviews.css";
import {
  useWindowVirtualizer,
  measureElement as rvMeasureElement,
} from "@tanstack/react-virtual";
import axios from "axios";

const RealReviews: React.FC = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastIdOzon, setLastIdOzon] = useState<string | null>(null);
  const [lastOzonReviewDate, setLastOzonReviewDate] = useState<string | null>(
    null
  );
  const [apiError, setApiError] = useState<string | null>(null);

  // Новые состояния для автоматической загрузки
  const [newReviews, setNewReviews] = useState<Set<string>>(new Set());
  const [autoRefreshInterval, setAutoRefreshInterval] =
    useState<NodeJS.Timeout | null>(null);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState<boolean>(
    () => {
      const saved = localStorage.getItem("isAutoRefreshEnabled");
      return saved !== null ? JSON.parse(saved) : true;
    }
  );
  const [newReviewsNotification, setNewReviewsNotification] = useState<{
    show: boolean;
    count: number;
  }>({ show: false, count: 0 });

  const [showAnalysis, setShowAnalysis] = useState<boolean>(() => {
    const saved = localStorage.getItem("showAnalysis");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Виртуализация списка с прокруткой окна
  const rows = reviews;
  const virtualizer = useWindowVirtualizer({
    count: rows.length + (hasMore ? 1 : 0),
    estimateSize: () => 300,
    overscan: 6,
    measureElement: rvMeasureElement,
    getItemKey: (index) => rows[index]?.id ?? index,
  });
  const virtualItems = virtualizer.getVirtualItems();
  const lastVirtualItem = virtualItems[virtualItems.length - 1];
  const lastIndex = lastVirtualItem ? lastVirtualItem.index : -1;

  // Храним предыдущий lastIndex, чтобы не триггерить автозагрузку на первом рендере
  const prevLastIndexRef = useRef<number | null>(null);

  // Автодогрузка при достижении низа
  useEffect(() => {
    if (lastIndex === -1) return;
    // Пропускаем автозагрузку на первом срабатывании эффекта
    if (prevLastIndexRef.current === null) {
      prevLastIndexRef.current = lastIndex;
      return;
    }

    const hasReachedEnd = lastIndex >= rows.length; // loader-row виден
    const scrolledFurther = lastIndex > prevLastIndexRef.current; // пользователь проскроллил дальше

    if (
      hasMore &&
      hasReachedEnd &&
      scrolledFurther &&
      !loading &&
      fetchReviewsRef.current
    ) {
      fetchReviewsRef.current(currentPage + 1, true);
    }

    prevLastIndexRef.current = lastIndex;
  }, [lastIndex, rows.length, hasMore, loading, currentPage]);

  // Хуки для анализа и ответа
  const { analyzingReviews, handleAnalyzeReview } = useReviewAnalysis();
  const {
    generatingResponses,
    handleGenerateResponse,
    handleRegenerateResponse,
  } = useReviewResponse();

  const fetchReviewsRef = useRef<
    ((page?: number, useLastId?: boolean) => Promise<void>) | null
  >(null);

  const checkApiSettings = useCallback(() => {
    // Проверяем наличие API ключей для обеих платформ
    const hasOzonApi = !!(user?.ozonApiKey && user?.ozonClientId);
    const hasWildberriesApi = !!user?.wildberriesApiKey;

    if (!hasOzonApi && !hasWildberriesApi) {
      setApiError(
        "API не настроен. Перейдите в настройки для настройки API Ozon или Wildberries."
      );
      return false;
    }
    return true;
  }, [user]);

  // Функция для загрузки новых отзывов
  const fetchNewReviews = useCallback(async () => {
    if (!checkApiSettings()) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      // Используем тот же эндпоинт что и для первой загрузки, но без параметров
      // чтобы получить самые свежие отзывы
      const universalUrl = `${getApiUrl(API_CONFIG.ENDPOINTS.UNIVERSAL_FILTERED_REVIEWS)}?`;

      const universalResponse = await fetch(universalUrl, { headers });

      if (universalResponse.ok) {
        const universalData = await universalResponse.json();

        // Определяем новые отзывы (которых нет в текущем списке)
        const existingReviewIds = new Set(reviews.map((r) => r.id));
        const newReviewsData = universalData.reviews.filter(
          (review: Review) => !existingReviewIds.has(review.id)
        );

        if (newReviewsData.length > 0) {
          // Показываем уведомление о новых отзывах
          setNewReviewsNotification({
            show: true,
            count: newReviewsData.length,
          });

          // Скрываем уведомление через 5 секунд
          setTimeout(() => {
            setNewReviewsNotification({ show: false, count: 0 });
          }, 5000);

          // Добавляем новые отзывы в начало списка и удаляем старые
          setReviews((prevReviews) => {
            // Добавляем новые отзывы в начало
            const updatedReviews = [...newReviewsData, ...prevReviews];

            // Удаляем самые старые отзывы, если общее количество превышает разумный лимит
            // Оставляем максимум 50 отзывов на странице
            const maxReviews = 50;
            const finalReviews = updatedReviews.slice(0, maxReviews);

            // Обновляем множество новых отзывов
            const newReviewIds = new Set(
              newReviewsData.map((r: Review) => r.id)
            );
            setNewReviews(newReviewIds as Set<string>);

            return finalReviews;
          });

          // Очищаем выделение новых отзывов через 30 секунд
          setTimeout(() => {
            setNewReviews(new Set());
          }, 30000);
        }

        // Не обновляем lastIdOzon и lastOzonReviewDate для новых отзывов,
        // так как мы хотим получать самые свежие отзывы
      }
    } catch (error) {
      console.error("Ошибка при загрузке новых отзывов:", error);
    }
  }, [reviews, checkApiSettings]);

  // Функция для запуска/остановки автоматического обновления
  const toggleAutoRefresh = useCallback(() => {
    if (isAutoRefreshEnabled) {
      // Останавливаем интервал
      if (autoRefreshInterval) {
        clearInterval(autoRefreshInterval);
        setAutoRefreshInterval(null);
      }
      setIsAutoRefreshEnabled(false);
      localStorage.setItem("isAutoRefreshEnabled", JSON.stringify(false));
    } else {
      // Запускаем интервал
      const interval = setInterval(fetchNewReviews, 30000);
      setAutoRefreshInterval(interval);
      setIsAutoRefreshEnabled(true);
      localStorage.setItem("isAutoRefreshEnabled", JSON.stringify(true));
    }
  }, [isAutoRefreshEnabled, autoRefreshInterval, fetchNewReviews]);

  const fetchReviews = useCallback(
    async (page: number = 1) => {
      if (!checkApiSettings()) {
        return;
      }

      setApiError(null);
      setLoading(true);

      try {
        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        };

        // Используем универсальный эндпоинт для получения отзывов с обеих платформ
        let universalUrl = `${getApiUrl(API_CONFIG.ENDPOINTS.UNIVERSAL_FILTERED_REVIEWS)}`;

        if (page > 1) {
          universalUrl += `?page=${page}`;
        } else {
          universalUrl += "?";
        }

        if (lastIdOzon) {
          universalUrl += `&lastIdOzon=${lastIdOzon}`;
        }

        if (lastOzonReviewDate) {
          universalUrl += `&lastOzonReviewDate=${lastOzonReviewDate}`;
        }

        const universalResponse = await fetch(universalUrl, { headers });

        if (universalResponse.ok) {
          const universalData = await universalResponse.json();

          if (page === 1) {
            setReviews(universalData.reviews);
          } else {
            setReviews((prev) => [...prev, ...universalData.reviews]);
          }

          setCurrentPage(page);
          setHasMore(universalData.hasMore || false);
          setLastIdOzon(universalData.lastIdOzon || null);
          setLastOzonReviewDate(universalData.lastOzonReviewDate || null);
        } else {
          setApiError("Ошибка при загрузке отзывов");
        }
      } catch (error) {
        setApiError("Ошибка при загрузке отзывов");
      } finally {
        setLoading(false);
      }
    },
    [lastIdOzon, lastOzonReviewDate, checkApiSettings]
  );

  useEffect(() => {
    fetchReviewsRef.current = fetchReviews;
  }, [fetchReviews]);

  useEffect(() => {
    setReviews([]);
    setHasMore(true);
    setCurrentPage(1);
    setLastIdOzon(null);
    setLastOzonReviewDate(null);
    if (fetchReviewsRef.current) {
      fetchReviewsRef.current(1);
    }
  }, []);

  // Запускаем автоматическое обновление при загрузке страницы только если оно включено
  useEffect(() => {
    if (checkApiSettings() && isAutoRefreshEnabled) {
      const interval = setInterval(fetchNewReviews, 30000);
      setAutoRefreshInterval(interval);

      // Очистка интервала при размонтировании
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [checkApiSettings, fetchNewReviews, isAutoRefreshEnabled]);

  // Очищаем интервал при отключении автообновления
  useEffect(() => {
    if (!isAutoRefreshEnabled && autoRefreshInterval) {
      clearInterval(autoRefreshInterval);
      setAutoRefreshInterval(null);
    }
  }, [isAutoRefreshEnabled, autoRefreshInterval]);

  // Функция для сохранения состояния переключателя в localStorage
  const handleShowAnalysisChange = useCallback((value: boolean) => {
    setShowAnalysis(value);
    localStorage.setItem("showAnalysis", JSON.stringify(value));
  }, []);

  // Функция для скролла наверх
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // Обработчик скролла для показа/скрытия кнопки
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollToTop(scrollTop > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // AI статус теперь глобально из AiStatusProvider

  // Обработчики для анализа и ответа
  const onAnalyzeReview = useCallback(
    async (
      reviewId: string,
      platform: "ozon" | "wildberries" | "yandex"
    ): Promise<boolean> => {
      // Автоматически включаем переключатель анализа при начале анализа
      if (!showAnalysis) {
        handleShowAnalysisChange(true);
      }

      const convertFromSharedReview = (sharedReviews: Review[]): Review[] => {
        return sharedReviews.map((sharedReview) => {
          const existingReview = reviews.find((r) => r.id === sharedReview.id);
          return {
            ...existingReview!,
            sentiment: sharedReview.sentiment,
            sentimentAnalysis: sharedReview.sentimentAnalysis,
            problems: sharedReview.problems,
            recommendations: sharedReview.recommendations,
            responseTemplate: sharedReview.responseTemplate,
            isAnalyzed: sharedReview.isAnalyzed,
            isResponded: sharedReview.isResponded,
            isFallback: sharedReview.isFallback,
          };
        });
      };

      const setSharedReviews = (updater: React.SetStateAction<Review[]>) => {
        setReviews((prevReviews) => {
          const newSharedReviews =
            typeof updater === "function" ? updater(prevReviews) : updater;
          return convertFromSharedReview(newSharedReviews);
        });
      };

      const ok = await handleAnalyzeReview(
        reviewId,
        setSharedReviews,
        platform
      );
      return ok;
    },
    [handleAnalyzeReview, reviews, showAnalysis, handleShowAnalysisChange]
  );

  const onGenerateResponse = useCallback(
    (reviewId: string) => {
      const convertFromSharedReview = (sharedReviews: Review[]): Review[] => {
        return sharedReviews.map((sharedReview) => {
          const existingReview = reviews.find((r) => r.id === sharedReview.id);
          return {
            ...existingReview!,
            sentiment: sharedReview.sentiment,
            sentimentAnalysis: sharedReview.sentimentAnalysis,
            problems: sharedReview.problems,
            recommendations: sharedReview.recommendations,
            responseTemplate: sharedReview.responseTemplate,
            isAnalyzed: sharedReview.isAnalyzed,
            isResponded: sharedReview.isResponded,
          };
        });
      };

      const setSharedReviews = (updater: React.SetStateAction<Review[]>) => {
        setReviews((prevReviews) => {
          const newSharedReviews =
            typeof updater === "function" ? updater(prevReviews) : updater;
          return convertFromSharedReview(newSharedReviews);
        });
      };

      handleGenerateResponse(reviewId, setSharedReviews);
    },
    [handleGenerateResponse, reviews]
  );

  const onRegenerateResponse = useCallback(
    (reviewId: string) => {
      const convertFromSharedReview = (sharedReviews: Review[]): Review[] => {
        return sharedReviews.map((sharedReview) => {
          const existingReview = reviews.find((r) => r.id === sharedReview.id);
          return {
            ...existingReview!,
            sentiment: sharedReview.sentiment,
            sentimentAnalysis: sharedReview.sentimentAnalysis,
            problems: sharedReview.problems,
            recommendations: sharedReview.recommendations,
            responseTemplate: sharedReview.responseTemplate,
            isAnalyzed: sharedReview.isAnalyzed,
            isResponded: sharedReview.isResponded,
          };
        });
      };

      const setSharedReviews = (updater: React.SetStateAction<Review[]>) => {
        setReviews((prevReviews) => {
          const newSharedReviews =
            typeof updater === "function" ? updater(prevReviews) : updater;
          return convertFromSharedReview(newSharedReviews);
        });
      };

      handleRegenerateResponse(reviewId, setSharedReviews);
    },
    [handleRegenerateResponse, reviews]
  );

  const fetchOzonReviews = async () => {
    const token = localStorage.getItem("token");
    const { data } = await axios.get<OzonReviewsResponse>(
      `${getApiUrl(API_CONFIG.ENDPOINTS.OZON_REVIEWS)}`,
      {
        params: { limit: 20 },
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );

    console.log("ozonReviews", data);
  };

  const fetchWildberriesReviews = async () => {
    const token = localStorage.getItem("token");
    const { data } = await axios.get<WildberriesReviewsResponse>(
      `${getApiUrl(API_CONFIG.ENDPOINTS.WILDBERRIES_REVIEWS)}`,
      {
        params: { take: 20, skip: 0 },
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );

    console.log("wildberriesReviews", data);
  };

  useEffect(() => {
    fetchOzonReviews();
    fetchWildberriesReviews();
  }, []);

  return (
    <div className="space-y-6">
      {/* Уведомление о новых отзывах */}
      {newReviewsNotification.show && (
        <div className="fixed top-4 right-4 z-50 animate-bounce">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-full shadow-lg border border-green-400 transform transition-all duration-300 hover:scale-105">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <svg
                  className="w-5 h-5 animate-spin"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-semibold">Новые отзывы!</span>
              </div>
              <div className="bg-white text-green-600 px-2 py-1 rounded-full text-sm font-bold animate-pulse">
                +{newReviewsNotification.count}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pt-2 px-2 sm:px-4 lg:px-6">
        {/* Заголовок и основные кнопки */}
        <div className="flex flex-col space-y-6">
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center justify-between space-x-6 w-full">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <svg
                    className="w-8 h-8 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    Отзывы
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Управление и анализ отзывов клиентов
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3">
                {/* Переключатель автоматического обновления */}
                <div className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200 shadow-sm h-11">
                  <div
                    className={`w-2.5 h-2.5 rounded-full ${
                      isAutoRefreshEnabled ? "bg-green-500" : "bg-gray-400"
                    }`}
                  ></div>
                  <span className="text-sm font-medium text-gray-700">
                    Автообновление
                  </span>
                  <button
                    onClick={toggleAutoRefresh}
                    className="ml-2 p-1 rounded hover:bg-gray-200 transition-colors duration-200"
                    title={
                      isAutoRefreshEnabled
                        ? "Остановить автообновление"
                        : "Запустить автообновление"
                    }
                  >
                    <svg
                      className="w-3.5 h-3.5 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </button>
                </div>

                {/* Переключатель показа анализа */}
                <label className="flex items-center space-x-3 px-4 py-3 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-100 transition-colors duration-200 h-11">
                  <span className="text-sm font-medium text-gray-700">
                    Показать анализ
                  </span>
                  <div className="relative inline-block w-10 h-6">
                    <input
                      type="checkbox"
                      checked={showAnalysis}
                      onChange={(e) =>
                        handleShowAnalysisChange(e.target.checked)
                      }
                      className="sr-only"
                    />
                    <div
                      className={`absolute inset-0 w-10 h-6 rounded-full transition-colors duration-200 ${
                        showAnalysis ? "bg-blue-600" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ${
                          showAnalysis ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {apiError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{apiError}</p>
              </div>
            </div>
          </div>
        )}

        {loading && reviews.length === 0 ? (
          <div className="flex justify-center items-center min-h-[240px]">
            <div className="flex flex-col items-center gap-3">
              <div
                className="h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600"
                aria-label="Загрузка"
              />
              <p className="text-sm text-gray-600">Загрузка отзывов…</p>
            </div>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex flex-col items-center space-y-4 p-8 bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl border border-gray-200">
              <div className="p-4 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-700">
                  Отзывов с текстом не найдено
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Попробуйте изменить параметры поиска
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 px-0 sm:px-0 lg:px-0">
            <div
              className="relative"
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: "100%",
              }}
            >
              {virtualItems.map((vi) => {
                const isLoaderRow = vi.index >= rows.length;
                const review = rows[vi.index];
                return (
                  <div
                    key={vi.key}
                    className="absolute left-0 right-0 px-0"
                    style={{
                      top: 0,
                      transform: `translateY(${vi.start}px)`,
                      height: `${vi.size}px`,
                    }}
                  >
                    <div
                      ref={virtualizer.measureElement}
                      data-index={vi.index}
                      className={`box-border ${vi.index < rows.length - 1 && vi.index < rows.length ? "pb-5" : "pb-0"}`}
                    >
                      {isLoaderRow ? (
                        <div className="flex justify-center items-center py-6">
                          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
                        </div>
                      ) : (
                        <div
                          className={`relative ${newReviews.has(review.id) ? "new-review-highlight" : ""}`}
                        >
                          <ReviewCard
                            review={review}
                            isNew={newReviews.has(review.id)}
                            analyzingReviews={analyzingReviews}
                            generatingResponses={generatingResponses}
                            onAnalyzeReview={onAnalyzeReview}
                            onGenerateResponse={onGenerateResponse}
                            onRegenerateResponse={onRegenerateResponse}
                            showAnalysis={showAnalysis}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {!hasMore && reviews.length > 0 && (
              <div className="text-center py-6">
                <div className="inline-flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-full border border-green-200">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-700">
                    Все отзывы с текстом загружены
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Кнопка скролла наверх */}
      <div
        className={`fixed bottom-6 right-6 z-50 transition-all duration-500 ease-in-out ${
          showScrollToTop
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
        }`}
      >
        <button
          onClick={scrollToTop}
          className="group relative p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:ring-offset-2"
          aria-label="Прокрутить наверх"
        >
          {/* Внутренний градиентный эффект */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Иконка */}
          <svg
            className="relative w-6 h-6 transform transition-transform duration-300 group-hover:-translate-y-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>

          {/* Тултип */}
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
            Наверх
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </button>
      </div>
    </div>
  );
};

export default RealReviews;
